import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ChevronDown, ChevronRight, Trophy, Sparkles } from "lucide-react";

const GOLD = "#C9A84C";

const POINT_SCALE = [
  { label: "1º lugar", value: 10 },
  { label: "2º lugar", value: 8 },
  { label: "3º lugar", value: 6 },
  { label: "4º lugar", value: 4 },
  { label: "5º lugar", value: 3 },
  { label: "6º lugar", value: 2 },
  { label: "Acima do 6º / Participação", value: 1 },
];

export default function CoachRanking() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(null);

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/coach-ranking", { params: { year } });
      setData(data);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [year]);

  const coaches = data?.coaches || [];
  const yearOptions = useMemo(() => {
    const arr = [];
    for (let y = now.getFullYear(); y >= now.getFullYear() - 5; y--) arr.push(y);
    return arr;
    // eslint-disable-next-line
  }, []);

  const toggle = (coachKey) => setExpanded((e) => (e === coachKey ? null : coachKey));

  return (
    <div data-testid="coach-ranking-page">
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <div>
          <div className="overline mb-2">Ranking</div>
          <h1 className="font-display text-5xl font-black tracking-tight">
            RANKING DE TREINADORES
          </h1>
          <div className="text-sm text-[var(--text-secondary)] mt-1">
            {isAdmin
              ? "Painel completo — pontuação acumulada por treinador."
              : "Sua pontuação anual como treinador."}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <label className="overline">Ano</label>
          <select
            data-testid="coach-year-select"
            className="input-field w-28"
            value={year}
            onChange={(e) => setYear(parseInt(e.target.value))}
          >
            {yearOptions.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div
        className="card p-4 mb-5 flex flex-wrap items-center gap-4 text-xs"
        style={{ borderColor: "rgba(201,168,76,0.25)" }}
      >
        <div className="flex items-center gap-2">
          <Sparkles size={13} style={{ color: GOLD }} />
          <span className="uppercase tracking-widest font-bold" style={{ color: GOLD }}>
            Como pontua
          </span>
        </div>
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-[var(--text-secondary)]">
          {POINT_SCALE.map((p) => (
            <span key={p.label} className="tabular-nums">
              <strong style={{ color: "#fff" }}>{p.value} pt</strong> · {p.label}
            </span>
          ))}
        </div>
        <div className="w-full text-[11px] text-[var(--text-secondary)]">
          Bônus de volume: +5 pontos quando o treinador leva 5+ atletas em um mesmo evento.
          Ranking zera a cada 01 de janeiro.
        </div>
      </div>

      <div className="card overflow-x-auto" data-testid="coach-ranking-table">
        <table className="table-dense">
          <thead>
            <tr>
              <th className="w-14">Pos.</th>
              <th>Treinador</th>
              <th>Equipe</th>
              <th className="text-right tabular-nums">Atletas</th>
              <th className="text-right tabular-nums">Eventos</th>
              <th className="text-right pr-4 tabular-nums">Total (pts)</th>
              <th className="w-10"></th>
            </tr>
          </thead>
          <tbody>
            {coaches.length === 0 && (
              <tr>
                <td colSpan={7} className="text-center py-8 text-[var(--text-secondary)]">
                  {busy
                    ? "Calculando..."
                    : isAdmin
                    ? "Nenhum treinador pontuou em " + year + " ainda."
                    : "Você ainda não tem pontos como treinador em " + year + "."}
                </td>
              </tr>
            )}
            {coaches.map((c) => {
              const isOpen = expanded === c.coach;
              return (
                <React.Fragment key={c.coach}>
                  <tr
                    data-testid={`coach-row-${c.coach.toLowerCase().replace(/\s+/g, "-")}`}
                    className="cursor-pointer hover:bg-white/5"
                    onClick={() => toggle(c.coach)}
                  >
                    <td className="tabular-nums font-display font-bold text-lg">
                      {c.posicao <= 3 ? (
                        <span style={{ color: GOLD }}>{c.posicao}º</span>
                      ) : (
                        `${c.posicao}º`
                      )}
                    </td>
                    <td>
                      <div className="font-medium flex items-center gap-2">
                        {c.posicao === 1 && <Trophy size={14} style={{ color: GOLD }} />}
                        {c.coach}
                      </div>
                    </td>
                    <td className="text-xs text-[var(--text-secondary)]">
                      {c.equipe || "—"}
                    </td>
                    <td className="text-right tabular-nums">
                      {c.unique_athlete_count}
                      <span className="text-[10px] text-[var(--text-secondary)] ml-1">
                        ({c.athlete_count} insc.)
                      </span>
                    </td>
                    <td className="text-right tabular-nums">{c.events.length}</td>
                    <td
                      className="text-right pr-4 tabular-nums font-display font-black text-lg"
                      style={{ color: GOLD }}
                      data-testid={`coach-total-${c.coach.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      {c.total_points}
                    </td>
                    <td className="text-right pr-2">
                      {isOpen ? (
                        <ChevronDown size={16} />
                      ) : (
                        <ChevronRight size={16} />
                      )}
                    </td>
                  </tr>
                  {isOpen && (
                    <tr>
                      <td colSpan={7} className="bg-black/40 px-6 py-4">
                        <div className="overline mb-3" style={{ color: GOLD }}>
                          Detalhamento por Evento
                        </div>
                        <div className="space-y-4">
                          {c.events.map((ev) => (
                            <div
                              key={ev.competition_id}
                              className="border border-white/5 rounded-sm p-3"
                              data-testid={`coach-event-${ev.competition_id}`}
                            >
                              <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                                <div>
                                  <div className="font-bold">{ev.competition_name}</div>
                                  <div className="text-[11px] text-[var(--text-secondary)]">
                                    {ev.date} · {ev.unique_athlete_count} atleta(s) inscrito(s)
                                    {ev.volume_bonus > 0 && (
                                      <span
                                        className="ml-2 px-1.5 py-0.5 text-[9px] font-bold tracking-widest"
                                        style={{
                                          background: GOLD,
                                          color: "#0a0a0a",
                                        }}
                                      >
                                        BÔNUS +5
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <div className="text-right tabular-nums">
                                  <div className="text-[11px] text-[var(--text-secondary)]">
                                    Colocações: {ev.points_from_placements}
                                    {ev.volume_bonus > 0 && ` + Bônus: ${ev.volume_bonus}`}
                                  </div>
                                  <div
                                    className="font-display font-black text-xl"
                                    style={{ color: GOLD }}
                                  >
                                    {ev.subtotal} pts
                                  </div>
                                </div>
                              </div>
                              <table className="w-full text-sm">
                                <tbody>
                                  {ev.placements.map((p) => (
                                    <tr key={p.athlete_id} className="border-t border-white/5">
                                      <td className="py-1 tabular-nums font-bold w-10">
                                        {p.posicao ? `${p.posicao}º` : "—"}
                                      </td>
                                      <td className="py-1">{p.athlete_name}</td>
                                      <td className="py-1 text-[11px] text-[var(--text-secondary)]">
                                        {p.category_name}
                                      </td>
                                      <td className="py-1 text-right pr-2 tabular-nums font-bold">
                                        {p.points} pt
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
